import React, { useMemo } from "react";
import {
    Button,
    Menu,
    MenuTrigger,
    MenuPopover,
    MenuList,
    MenuItem,
    MenuDivider
} from "@fluentui/react-components";
import {
    NavigationRegular,
    HomeRegular,
    SignOutRegular,
    PersonRegular,
    PeopleSearchRegular,
    PersonAddRegular,
    PeopleTeamRegular,
    SettingsCogMultipleRegular,
    PeopleRegular,
    ChevronRightRegular,
    ClipboardTaskRegular,
    TaskListSquareLtr24Regular,
    TagMultipleRegular,
    CartRegular,
    SparkleRegular,
    BoxMultipleRegular,
    LockClosedRegular,
    DatabaseRegular,
    GlobeRegular,
} from "@fluentui/react-icons";
import { useAppContext } from "@/app/AppContext";
import { PinnedContainer } from "@/components/PinnedContainer";
import { NavMenuItem } from "@/models/models";
import { AuthRoles, hasRole } from "@/auth/AuthRoles";

/**
 * Main navigation menu items.
 *
 * IMPORTANT: All core views in the application should have a corresponding
 * entry in this menu. This ensures users can navigate to all available
 * features through the main navigation drawer.
 *
 * Each item specifies auth_roles to control visibility based on user permissions.
 * Items are filtered at render time - users only see links they have access to.
 * Items with children render as expandable sub-menus.
 */
const mainMenuItems: NavMenuItem[] = [
    {
        label: 'Home',
        path: '/home',
        icon: <HomeRegular />,
        auth_roles: [AuthRoles.ANY],
    },
    {
        label: 'My Profile',
        path: '/profile',
        icon: <PersonRegular />,
        auth_roles: [AuthRoles.ANY],
    },
    {
        label: 'Assessment',
        path: '/assessment',
        icon: <ClipboardTaskRegular />,
        auth_roles: [AuthRoles.ANY],
    },
    {
        label: 'Purchase Assessments',
        path: '/purchase',
        icon: <CartRegular />,
        auth_roles: [AuthRoles.ANY],
    },
    {
        label: 'Reset Password',
        path: '/settings',
        icon: <LockClosedRegular />,
        auth_roles: [AuthRoles.ANY],
        dividerAfter: true,
    },
    {
        label: 'Administration',
        icon: <SettingsCogMultipleRegular />,
        auth_roles: [AuthRoles.ADMIN],
        children: [
            {
                label: 'Users',
                isSectionHeader: true,
                auth_roles: [AuthRoles.ADMIN],
            },
            {
                label: 'User Lookup',
                path: '/admin/users',
                icon: <PeopleSearchRegular />,
                auth_roles: [AuthRoles.ADMIN],
            },
            {
                label: 'Create User',
                path: '/admin/usercreate',
                icon: <PersonAddRegular />,
                auth_roles: [AuthRoles.ADMIN],
            },
            {
                label: 'Assessments',
                isSectionHeader: true,
                auth_roles: [AuthRoles.ADMIN],
            },
            {
                label: 'Manage Assessments',
                path: '/admin/assessmenttypes',
                icon: <TaskListSquareLtr24Regular />,
                auth_roles: [AuthRoles.ADMIN],
            },
            {
                label: 'Assessment Bundles',
                path: '/admin/bundles',
                icon: <BoxMultipleRegular />,
                auth_roles: [AuthRoles.ADMIN],
            },
            {
                label: 'Commerce',
                isSectionHeader: true,
                auth_roles: [AuthRoles.ADMIN],
            },
            {
                label: 'Discount Codes',
                path: '/admin/discountcodes',
                icon: <TagMultipleRegular />,
                auth_roles: [AuthRoles.ADMIN],
            },
            {
                label: 'System',
                isSectionHeader: true,
                auth_roles: [AuthRoles.ADMIN],
            },
            {
                label: 'MCP Server Access',
                path: '/admin/agentkeys',
                icon: <SparkleRegular />,
                auth_roles: [AuthRoles.ADMIN],
            },
            {
                label: 'Reference Data',
                path: '/admin/referencedata',
                icon: <DatabaseRegular />,
                auth_roles: [AuthRoles.ADMIN],
            },
            {
                label: 'Web Content',
                path: '/admin/webcontent',
                icon: <GlobeRegular />,
                auth_roles: [AuthRoles.ADMIN],
            },
        ],
    },
    {
        label: 'Training Coach',
        icon: <PeopleTeamRegular />,
        auth_roles: [AuthRoles.TRAINING_COACH],
        children: [
            {
                label: 'My Supervisors',
                path: '/coach/dashboard',
                icon: <PeopleRegular />,
                auth_roles: [AuthRoles.TRAINING_COACH],
            },
        ],
    },
    {
        label: 'Supervisor',
        icon: <PeopleTeamRegular />,
        auth_roles: [AuthRoles.SUPERVISOR],
        children: [
            {
                label: 'My Users',
                path: '/supervisor/users',
                icon: <PeopleRegular />,
                auth_roles: [AuthRoles.SUPERVISOR],
            },
            {
                label: 'Add User',
                path: '/supervisor/usercreate',
                icon: <PersonAddRegular />,
                auth_roles: [AuthRoles.SUPERVISOR],
            },
            {
                label: 'Purchase for Users',
                path: '/purchase/batch',
                icon: <CartRegular />,
                auth_roles: [AuthRoles.SUPERVISOR],
            },
        ],
    },
];

/**
 * Recursively filter menu items based on user's roles.
 * Also filters children arrays.
 */
const filterMenuItems = (items: NavMenuItem[], userRoles: string[]): NavMenuItem[] => {
    return items
        .filter(item => hasRole(userRoles, item.auth_roles))
        .map(item => {
            if (item.children && item.children.length > 0) {
                const filteredChildren = filterMenuItems(item.children, userRoles);
                // Only include parent if it has visible children
                if (filteredChildren.length > 0) {
                    return { ...item, children: filteredChildren };
                }
                return null;
            }
            return item;
        })
        .filter((item): item is NavMenuItem => item !== null);
};

const AppHeader: React.FC = () => {
    const { user, fetchData, setCurrentPath, pageTitle } = useAppContext();
    const [expandedMenus, setExpandedMenus] = React.useState<Set<string>>(new Set());
    const [menuOpen, setMenuOpen] = React.useState(false);
    const submenuClickRef = React.useRef(false);

    // Filter menu items based on user's roles
    const visibleMenuItems = useMemo(() => {
        if (!user) return [];
        return filterMenuItems(mainMenuItems, user.roles);
    }, [user]);

    const toggleSubmenu = (label: string) => {
        // Mark that we're toggling a submenu - this prevents menu from closing
        submenuClickRef.current = true;
        setExpandedMenus(prev => {
            const newSet = new Set(prev);
            if (newSet.has(label)) {
                newSet.delete(label);
            } else {
                newSet.add(label);
            }
            return newSet;
        });
    };

    const handleMenuOpenChange = (_: unknown, data: { open: boolean }) => {
        // If we just clicked a submenu toggle, don't close the menu
        if (submenuClickRef.current) {
            submenuClickRef.current = false;
            return;
        }
        setMenuOpen(data.open);
    };

    const onLogout = () => {
        setMenuOpen(false);
        setCurrentPath('/logout');
    };

    const navigateTo = (path: string) => {
        setMenuOpen(false);
        setCurrentPath(path);
    };

    return (
        <PinnedContainer position="top">
            <header className="app-header">
                {user && (
                    <Menu open={menuOpen} onOpenChange={handleMenuOpenChange}>
                        <MenuTrigger disableButtonEnhancement>
                            <Button
                                className="menu-trigger header-icon-btn"
                                icon={<NavigationRegular />}
                                appearance="transparent"
                            />
                        </MenuTrigger>
                        <MenuPopover>
                            <MenuList>
                                {visibleMenuItems.map((item) => {
                                    const key = item.path || item.label;

                                    // If item has children, render as expandable submenu
                                    // Use a custom div instead of MenuItem to prevent menu close on click
                                    if (item.children && item.children.length > 0) {
                                        const isExpanded = expandedMenus.has(item.label);
                                        return (
                                            <React.Fragment key={key}>
                                                <div
                                                    className="fui-MenuItem submenu-parent"
                                                    role="menuitem"
                                                    tabIndex={0}
                                                    onClick={() => toggleSubmenu(item.label)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                            e.preventDefault();
                                                            toggleSubmenu(item.label);
                                                        }
                                                    }}
                                                >
                                                    <span className="fui-MenuItem__icon">{item.icon}</span>
                                                    <span className="fui-MenuItem__content">{item.label}</span>
                                                    <span className="fui-MenuItem__secondaryContent">
                                                        <ChevronRightRegular
                                                            style={{
                                                                transform: isExpanded ? 'rotate(90deg)' : 'none',
                                                                transition: 'transform 0.2s'
                                                            }}
                                                        />
                                                    </span>
                                                </div>
                                                {isExpanded && item.children.map((child) => (
                                                    child.isSectionHeader ? (
                                                        <div
                                                            key={`section-${child.label}`}
                                                            style={{
                                                                paddingLeft: '2rem',
                                                                paddingRight: '12px',
                                                                paddingTop: '8px',
                                                                paddingBottom: '4px',
                                                                fontSize: '11px',
                                                                fontWeight: 600,
                                                                textTransform: 'uppercase',
                                                                letterSpacing: '0.05em',
                                                                color: 'var(--colorNeutralForeground3)',
                                                            }}
                                                        >
                                                            {child.label}
                                                        </div>
                                                    ) : (
                                                        <MenuItem
                                                            key={child.path}
                                                            icon={child.icon}
                                                            onClick={() => child.path && navigateTo(child.path)}
                                                            style={{ paddingLeft: '2rem' }}
                                                        >
                                                            {child.label}
                                                        </MenuItem>
                                                    )
                                                ))}
                                                {item.dividerAfter && <MenuDivider />}
                                            </React.Fragment>
                                        );
                                    }

                                    // Regular menu item
                                    return (
                                        <React.Fragment key={key}>
                                            <MenuItem
                                                icon={item.icon}
                                                onClick={() => item.path && navigateTo(item.path)}
                                            >
                                                {item.label}
                                            </MenuItem>
                                            {item.dividerAfter && <MenuDivider />}
                                        </React.Fragment>
                                    );
                                })}
                                {visibleMenuItems.length > 0 && visibleMenuItems[visibleMenuItems.length - 1]?.dividerAfter !== true && (
                                    <MenuDivider />
                                )}
                                <MenuItem
                                    icon={<SignOutRegular />}
                                    onClick={onLogout}
                                >
                                    Logout
                                </MenuItem>
                            </MenuList>
                        </MenuPopover>
                    </Menu>
                )}

                <div className="header-brand">
                    <h1>Total Ministry Health</h1>
                    {pageTitle && <span className="page-title">{pageTitle}</span>}
                </div>

                <div className="header-spacer" />

            </header>
        </PinnedContainer>
    );
};

export default AppHeader;
